/**
 * RecipePage — public landing for the "Recipe of the Week".
 *
 * Reached from the Vote & Comment CTA in the newsletter and from any
 * referral / share link. Shows the recipe in full, lets readers up/down
 * vote, post a comment ("best comment wins reward points"), and see the
 * three suggested Brisbane restaurants where they can try the dish.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import "./recipe.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function RecipePage() {
  const { rid } = useParams();
  const [recipe, setRecipe] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [comments, setComments] = useState([]);
  const [voteState, setVoteState] = useState(null); // 'up' | 'down' | null
  const [voteTotals, setVoteTotals] = useState({ up: 0, down: 0 });
  const [draftComment, setDraftComment] = useState({ author: "", email: "", text: "" });
  const [busy, setBusy] = useState("");
  const [thanks, setThanks] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!rid) return;
    axios.get(`${API}/api/newsletter/recipes/${rid}`)
      .then(({ data }) => {
        setRecipe(data);
        setVoteTotals({ up: data.votes_up || 0, down: data.votes_down || 0 });
      })
      .catch(() => setErr("That recipe isn't available."));
    // The match call also lazy-creates suggestions if none yet — harmless.
    axios.post(`${API}/api/newsletter/recipes/${rid}/match`, { city: "brisbane", limit: 3 })
      .then(({ data }) => setSuggestions(data.suggestions || []))
      .catch(() => { /* non-fatal */ });
    axios.get(`${API}/api/newsletter/recipes/${rid}/comments`)
      .then(({ data }) => setComments(data.comments || []))
      .catch(() => { /* non-fatal */ });
  }, [rid]);

  const submitVote = async (choice) => {
    setBusy("Voting…"); setErr("");
    try {
      const { data } = await axios.post(`${API}/api/newsletter/recipes/${rid}/vote`, { vote: choice });
      setVoteTotals(data.totals);
      setVoteState(choice);
      setThanks("Thanks for voting!");
    } catch (e) {
      if (e?.response?.status === 409) {
        setErr("You've already voted on this recipe.");
        setVoteState(choice);
      } else {
        setErr("Vote failed — try again in a moment.");
      }
    } finally { setBusy(""); }
  };

  const submitComment = async (e) => {
    e.preventDefault();
    if (!draftComment.text.trim()) return;
    setBusy("Posting comment…"); setErr("");
    try {
      await axios.post(`${API}/api/newsletter/recipes/${rid}/comment`, draftComment);
      // Refresh comments
      const { data } = await axios.get(`${API}/api/newsletter/recipes/${rid}/comments`);
      setComments(data.comments || []);
      setDraftComment({ author: "", email: "", text: "" });
      setThanks("Comment posted! Best comment each week wins reward points.");
    } catch {
      setErr("Couldn't post comment — try again.");
    } finally { setBusy(""); }
  };

  if (err && !recipe) return (
    <div className="rp-wrap"><div className="rp-card"><p>{err}</p><Link to="/" className="rp-btn">Back home</Link></div></div>
  );
  if (!recipe) return <div className="rp-wrap"><p>Loading recipe…</p></div>;

  const meta = [
    recipe.serves && `Serves ${recipe.serves}`,
    recipe.prep_minutes && `Prep ${recipe.prep_minutes}m`,
    recipe.cook_minutes && `Cook ${recipe.cook_minutes}m`,
    recipe.cuisine,
  ].filter(Boolean).join(" · ");

  return (
    <div className="rp-wrap" data-testid="recipe-page">
      <div className="rp-bar">
        <Link to="/" className="rp-back">← Local Hustle Brisbane</Link>
      </div>

      <article className="rp-card">
        <span className="rp-eyebrow">🍳 Recipe of the Week</span>
        <h1 data-testid="recipe-title">{recipe.name}</h1>
        {recipe.blurb && <p className="rp-blurb">{recipe.blurb}</p>}
        {recipe.image_url && <img src={recipe.image_url} alt={recipe.name} className="rp-hero" />}
        <p className="rp-meta">{meta}</p>

        <div className="rp-cols">
          <div>
            <h3>Ingredients</h3>
            <ul>{(recipe.ingredients || []).map((i, idx) => <li key={idx}>{i}</li>)}</ul>
          </div>
          <div>
            <h3>Method</h3>
            <ol>{(recipe.steps || []).map((s, idx) => <li key={idx}>{s}</li>)}</ol>
          </div>
        </div>

        {recipe.chef_tip && <p className="rp-tip"><strong>Chef tip:</strong> {recipe.chef_tip}</p>}
        {recipe.wine_pairing && <p className="rp-pairing"><strong>🍷 Pairing:</strong> {recipe.wine_pairing}</p>}
      </article>

      {/* Voting */}
      <section className="rp-vote">
        <h2>Did you try it? Vote on this week's recipe.</h2>
        <div className="rp-vote__row">
          <button className={`rp-vote__btn ${voteState === "up" ? "is-active" : ""}`} onClick={() => submitVote("up")} disabled={!!voteState || !!busy} data-testid="vote-up">
            👍 Loved it · {voteTotals.up}
          </button>
          <button className={`rp-vote__btn rp-vote__btn--down ${voteState === "down" ? "is-active" : ""}`} onClick={() => submitVote("down")} disabled={!!voteState || !!busy} data-testid="vote-down">
            👎 Not for me · {voteTotals.down}
          </button>
        </div>
        {thanks && <p className="rp-ok">{thanks}</p>}
        {err && <p className="rp-err">{err}</p>}
      </section>

      {/* Suggested restaurants */}
      {suggestions.length > 0 && (
        <section className="rp-sugg">
          <h2>Try it at a Brisbane restaurant tonight</h2>
          <p className="rp-sugg__sub">These local kitchens cook the cuisine. Show this page to claim your member discount where available.</p>
          {suggestions.map((s) => (
            <a key={s.slug} href={`https://hustleplaza.au/r/${s.slug}`} className="rp-sugg__item" target="_blank" rel="noopener noreferrer">
              {s.hero_image_url && <img src={s.hero_image_url} alt="" />}
              <div>
                <strong>{s.name}</strong>
                <div className="rp-sugg__meta">{[s.cuisine, s.suburb, s.rating && `★ ${s.rating}`].filter(Boolean).join(" · ")}</div>
                <span className="rp-sugg__link">See menu &amp; book →</span>
              </div>
            </a>
          ))}
        </section>
      )}

      {/* Comments */}
      <section className="rp-comments">
        <h2>Comments — best one wins reward points</h2>
        <form onSubmit={submitComment} className="rp-form" data-testid="recipe-comment-form">
          <input placeholder="Your name (or stay anonymous)" value={draftComment.author} onChange={(e) => setDraftComment({ ...draftComment, author: e.target.value })} />
          <input type="email" placeholder="Email (private — for reward points)" value={draftComment.email} onChange={(e) => setDraftComment({ ...draftComment, email: e.target.value })} />
          <textarea rows={4} placeholder="What did you think? Did you tweak it? Tell us your story…" value={draftComment.text} onChange={(e) => setDraftComment({ ...draftComment, text: e.target.value })} required />
          <button type="submit" className="rp-btn" disabled={!!busy} data-testid="recipe-comment-submit">{busy || "Post comment"}</button>
        </form>

        {comments.length === 0 && <p className="rp-empty">Be the first to comment.</p>}
        {comments.map((c) => (
          <div key={c.id} className={`rp-comment ${c.status === "winner" ? "is-winner" : ""}`}>
            <strong>{c.author || "Anonymous"}</strong>
            {c.status === "winner" && <span className="rp-winner">🏆 winner · +{c.reward_points || 100} pts</span>}
            <p>{c.text}</p>
            <small>{new Date(c.created_at).toLocaleString("en-AU")}</small>
          </div>
        ))}
      </section>

      <footer className="rp-foot">
        <p>Want recipes like this every week? <a href="https://hustleplaza.au/">Join the Local Hustle Brisbane newsletter</a> — free.</p>
      </footer>
    </div>
  );
}
